import { Download, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { folderHelperStatus, isMac } from './folderMode';
import { beginFinderClick, finderCommand } from './finderRelay';
import './folder-helper-actions.css';
import './folder-helper-guide.css';
import './finder-helper.css';

export const FOLDER_HELPER_DOWNLOAD = '/downloads/SkrepkaFolders-a83162ca919cf194.exe';
export const MAC_FOLDER_HELPER_DOWNLOAD = '/downloads/SkrepkaFolders-mac-3f9817016a910c6f.zip';
const GUIDE_UNTIL = Date.parse('2026-11-11T00:00:00+05:00');

export function FolderHelperActions() {
  const mac = isMac();
  const [ready, setReady] = useState(false);
  const [update, setUpdate] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [notice, setNotice] = useState('');
  const [guideVisible, setGuideVisible] = useState(() => Date.now() < GUIDE_UNTIL);
  const alive = useRef(false);
  const attempt = useRef(0);
  useEffect(() => {
    alive.current = true;
    const check = () => { void folderHelperStatus().then(status => {
      if (!alive.current) return;
      setGuideVisible(Date.now() < GUIDE_UNTIL);
      setReady(status === 'ready');
      setUpdate(status === 'update');
      if (status === 'ready') setNotice('');
    }); };
    check();const timer = setInterval(check, 2500);window.addEventListener('focus', check);
    return () => { alive.current = false; clearInterval(timer); window.removeEventListener('focus', check); };
  }, []);
  const launch = async () => {
    const id = ++attempt.current;
    setLaunching(true);setNotice('Запускаем помощник папок…');
    const until = Date.now() + 15000;
    let found = false;
    while (alive.current && attempt.current === id && Date.now() < until) {
      found = await folderHelperStatus() === 'ready';
      if (found) break;
      await new Promise(resolve => setTimeout(resolve, 700));
    }
    if (!alive.current || attempt.current !== id) return;
    setReady(found);setLaunching(false);
    setNotice(found ? 'Помощник папок работает.' : 'Откройте скачанный установщик. Если помощник уже работает, разрешите браузеру доступ к локальной сети.');
  };
  const connectFinder = async () => {
    const id = ++attempt.current;
    const nonce = beginFinderClick();
    setLaunching(true); setNotice('Разрешите Safari открыть помощник папок…');
    const found = await finderCommand(nonce);
    if (!alive.current || attempt.current !== id) return;
    setReady(found); setLaunching(false);
    setNotice(found ? 'Помощник подключён. Теперь можно нажимать жёлтые папки.' : 'Не удалось связаться с помощником. Откройте установленное приложение «Скрепка · Папки» и повторите подключение.');
  };
  return <div className="sx-folder-helper">
    <p>Помощник папок открывает {mac ? 'Finder' : 'проводник Windows'} в студии и через VPN. Установите один раз — дальше он запускается сам.</p>
    {mac && <small className="sx-finder-compatibility">Для macOS 13 и новее, Intel и Apple Silicon. После установки нажмите «Подключить помощник» и разрешите Safari открыть приложение.</small>}
    <div className="sx-folder-helper-actions">
      {ready && !mac ? <button type="button" disabled><Download size={17}/>Установлен</button> : <a href={mac ? MAC_FOLDER_HELPER_DOWNLOAD : FOLDER_HELPER_DOWNLOAD} download={mac ? 'SkrepkaFolders-mac.zip' : 'SkrepkaFolders.exe'} onClick={() => setNotice(mac ? 'Распакуйте скачанный архив, откройте из него «Скрепка · Папки» и нажмите «Установить» или «Обновить». Версия 1.0.1, для Intel и Apple Silicon.' : 'Откройте скачанный файл и нажмите «Установить» или «Обновить». Права администратора не нужны.')}><Download size={17}/>{update || (mac && ready) ? 'Обновить помощник' : 'Установить для папок'}</a>}
      {mac ? <button type="button" disabled={launching} onClick={() => { void connectFinder(); }}><Play size={17}/>{launching ? 'Подключаем…' : ready ? 'Проверить подключение' : 'Подключить помощник'}</button> : <a href="skrepka-folders://launch" aria-disabled={launching || undefined} onClick={event => {
        if (launching) event.preventDefault();
        else if (!ready) void launch();
      }}><Play size={17}/>{ready ? 'Открыть помощник' : launching ? 'Запускаем…' : 'Запустить'}</a>}
    </div>
    {(guideVisible || mac) && <details className="sx-folder-helper-guide">
      <summary>Как настроить открытие папок</summary>
      <ol>
        <li>{mac ? 'Нажмите «Установить для папок» или «Обновить помощник» и дождитесь скачивания.' : 'Нажмите «Установить для папок» и дождитесь скачивания.'}</li>
        <li>{mac ? 'Распакуйте скачанный архив в «Загрузках», откройте из него «Скрепка · Папки» и нажмите «Установить» или «Обновить». Помощник появится в папке «Программы» вашего пользователя.' : 'Откройте скачанный файл из загрузок браузера или Проводника Windows. Нажмите «Установить» или «Обновить».'}</li>
        {mac && <li>Вернитесь сюда, нажмите «Подключить помощник» и разрешите Safari открыть приложение. Дождитесь сообщения о подключении.</li>}
        <li>Выберите в этом меню «Авто» и нажмите жёлтую папку у ассета.</li>
        <li>Разрешите браузеру открыть помощник. Если есть галочка «Всегда разрешать для этого сайта», поставьте её.</li>
      </ol>
      <p>Установите помощник один раз на каждом своём компьютере. Дальше он запускается сам при входе в {mac ? 'macOS' : 'Windows'}.</p>
      <p>В студии папки открываются в {mac ? 'Finder' : 'Windows'}. Дома сначала подключите VPN до студии. Нужная папка должна быть доступна и в {mac ? 'Finder. При первом подключении macOS может запросить имя и пароль студийной сетевой папки' : 'обычном Проводнике Windows'}.</p>
      <p>Если сетевая папка недоступна, режим «Авто» откроет браузерный проводник. Выбрать «Браузер» вручную можно в этом же меню.</p>
    </details>}
    <small>Это отдельный помощник, без передачи управления компьютером.</small>
    {notice && <small role="status">{notice}</small>}
  </div>;
}
